import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import lscache from "lscache";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl";
import { toast } from "react-toastify";
import { useUser } from "../../contexts/UserContext";
import { useApartmentFunctions } from "../../hooks/apartments/useApartmentFunctions";
import { useDeleteHandler } from "../../hooks/useDeleteHandler";
import { useEditHandler } from "../../hooks/useEditHandler";
import apartmentService from "../../services/apartment-service";
import IApartment, {
  ApartmentComponentProps,
} from "../../types/apartment-type";
import { accommodationTypes, groups } from "../../utils/accommodationTypes";
import BaseCard from "../Handlers/CardHandler";
import MeasurementSwitch from "../ui-Components/MeasurementSwitch";
import ApartmentForm from "./ApartmentForm";

const ApartmentComponent: React.FC<ApartmentComponentProps> = ({
  canDelete,
  listingType,
}) => {
  const intl = useIntl();
  const [selectedGroups, setSelectedGroups] = useState<string[]>(["all"]);
  const { user, userId } = useUser();

  const initialFormData = {
    rent: "",
    accommodationType: "apartment",
    listingType: listingType,
    duration: "long-term",
    currency: "EUR",
    title: "",
    description: "",
    size: "",
    address: "",
    city: "",
    country: "Finland",
    photos: [],
    availableFrom: dayjs(),
    datePosted: dayjs(),
    genderPreference: "any",
  };

  const [formData, setFormData] = useState<IApartment>(initialFormData);

  const [measurementUnit, setMeasurementUnit] = useState<"m²" | "sqft">("m²");

  const {
    isLoading,
    longTerm,
    shortStays,
    setApartments,
    convertSize,
    loadApartments,
  } = useApartmentFunctions();

  const { handleEditClick, editing, setEditing, showForm, setShowForm } =
    useEditHandler<IApartment>(apartmentService);

  const { handleDelete } = useDeleteHandler<IApartment>(apartmentService);

  const initialFormDataRef = useRef(initialFormData);
  const prevListingTypeRef = useRef(listingType);
  const listingTypeMemo = useMemo(() => ({ listingType }), [listingType]);

  useEffect(() => {
    const cachedFormData: Partial<IApartment> = {};
    Object.keys(initialFormDataRef.current).forEach((key) => {
      const apartmentKey = "apartment-" + key;
      const cachedValue = lscache.get(apartmentKey);
      if (cachedValue != null) {
        cachedFormData[key as keyof typeof cachedFormData] = cachedValue; // keyof typeof: "Trust me, this key is one of the valid labels in the cachedFormData box."
      }
    });

    setFormData((prevFormData) => ({
      ...prevFormData,
      ...cachedFormData,
    }));

    const cachedMeasurementUnit = lscache.get("measurementUnit");
    if (cachedMeasurementUnit !== null) {
      setMeasurementUnit(cachedMeasurementUnit);
    }
  }, [user, userId]);

  useEffect(() => {
    if (listingType !== prevListingTypeRef.current) {
      setFormData({
        ...initialFormDataRef.current,
        listingType: listingTypeMemo.listingType,
      });
      if (editing) {
        setShowForm(false);
        setEditing(false);
      }
      prevListingTypeRef.current = listingType; // Update the previous listing type reference
    }
  }, [
    listingType,
    editing,
    listingTypeMemo.listingType,
    setShowForm,
    setEditing,
  ]);

  const handleUnitChange = (newUnit: "m²" | "sqft") => {
    setMeasurementUnit(newUnit);

    if (formData.size != null && parseFloat(formData.size) > 0) {
      const size = parseFloat(formData.size);

      // Assuming convertSize is a utility function to handle size conversion
      const newSize = convertSize(size.toString(), measurementUnit, newUnit);

      setFormData({ ...formData, size: newSize });
      lscache.set("measurementUnit", newUnit, 30);
      lscache.set("apartment-" + formData.size, newSize, 30);
    }
  };

  const handleEditApartment = async (apartmentData: IApartment) => {
    const data = await handleEditClick(apartmentData);
    if (data) {
      setFormData(data);
    }
  };
  const handleDeleteApartment = async (apartmentData: IApartment) => {
    const data = await handleDelete(apartmentData);
    if (data) {
      const updatedApartments = filteredApartments.filter(
        (currentApartment) => currentApartment.id !== apartmentData.id
      );
      setApartments(updatedApartments);
      loadApartments();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const sizeInSqM =
        measurementUnit === "m²"
          ? formData.size
          : (parseFloat(formData.size!) * 0.092903).toFixed(1).toString();

      const apartmentContent: IApartment = {
        id: formData.id,
        rent: formData.rent,
        accommodationType: formData.accommodationType,
        listingType: formData.listingType,
        duration: formData.duration,
        currency: formData.currency,
        size: sizeInSqM,
        address: formData.address,
        title: formData.title,
        description: formData.description,
        city: formData.city,
        country: formData.country,
        photos: formData.photos,
        creatorId: userId,
        availableFrom: formData.availableFrom,
        datePosted: formData.datePosted,
        genderPreference: formData.genderPreference,
      };
      if (editing === true) {
        await apartmentService.updateApartment(
          apartmentContent.id!,
          apartmentContent
        );
        toast.success("Apartment updated successfully!", {
          position: "bottom-right",
        });
      } else {
        await apartmentService.listApartment(apartmentContent);
        toast.success("Apartment listed successfully!", {
          position: "bottom-right",
        });
      }
      setEditing(false);
      setFormData(initialFormData);
      Object.keys(initialFormData).forEach((key) => {
        lscache.set(
          "apartment-" + key,
          initialFormData[key as keyof typeof initialFormData]
        );
      });
      setShowForm(false);
      loadApartments();
    } catch (error) {
      console.error("Error listing apartment:", error);
      toast.error(
        "Failed to list apartment. Please check the details and try again.",
        {
          position: "bottom-right",
        }
      );
    }
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target;
    let newSelectedGroups: string[] = [];

    if (value === "all") {
      newSelectedGroups = checked ? ["all"] : [];
    } else {
      newSelectedGroups = checked
        ? selectedGroups.filter((group) => group !== "all").concat(value)
        : selectedGroups.filter((group) => group !== value);
    }

    setSelectedGroups(newSelectedGroups);
  };

  const filteredApartments = useMemo(() => {
    // Check whether listingType is long-term or short-stay and return only those listings
    const lenghtOfTenancy = listingType === "long-term" ? longTerm : shortStays;
    // filter by the groups marked in the checkboxes
    return lenghtOfTenancy.filter((apartment: IApartment) => {
      const accommodationType = accommodationTypes.find(
        (type) => type.value === apartment.accommodationType
      );
      if (accommodationType) {
        return (
          selectedGroups.includes("all") ||
          selectedGroups.includes(accommodationType.group)
        );
      } else {
        console.warn(
          `Accommodation type ${apartment.accommodationType} not found in utility.`
        );
        return selectedGroups.includes("all");
      }
    });
  }, [selectedGroups, listingType, longTerm, shortStays]);

  return (
    <div className="container">
      {isLoading && <div>...Loading</div>}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <header>
            <Typography variant="h3">
              {listingType === "long-term" ? (
                <FormattedMessage id="available-apartments" />
              ) : (
                <FormattedMessage id="short-stays" />
              )}
            </Typography>
          </header>
        </Grid>
        <Grid item xs={12} md={4}>
          <Button
            id="toggle-form-button"
            data-testid="toggle-form-button"
            sx={{ mt: 2, mb: 2, width: { xs: "100%", sm: "90%" } }}
            variant="contained"
            onClick={() => setShowForm(!showForm)}
          >
            {showForm === null && (
              <Typography variant="body1">
                <FormattedMessage id="list-apartment" />
              </Typography>
            )}
            {showForm !== null && (
              <Typography variant="body1">
                {showForm ? (
                  <FormattedMessage id="hide" />
                ) : (
                  <FormattedMessage id="show" />
                )}
              </Typography>
            )}
          </Button>
        </Grid>
        <Grid item xs={12}>
          {showForm && (
            <ApartmentForm
              editing={editing}
              formData={formData}
              setFormData={setFormData}
              handleSubmit={handleSubmit}
              measurementUnit={measurementUnit}
              rentalType={listingTypeMemo}
              handleUnitChange={handleUnitChange}
              setMeasurementUnit={setMeasurementUnit}
            />
          )}
        </Grid>
        <Grid item xs={12}>
          <FormControl component="fieldset">
            <FormGroup row>
              {groups.map((group) => (
                <FormControlLabel
                  key={group}
                  control={
                    <Checkbox
                      checked={selectedGroups.includes(group)}
                      onChange={handleCheckboxChange}
                      value={group}
                    />
                  }
                  label={intl.formatMessage({
                    id: group,
                    defaultMessage: group,
                  })}
                />
              ))}
            </FormGroup>
          </FormControl>
        </Grid>
        {filteredApartments.map((apartment) => {
          const cardDetailsTop = (
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item>
                <Typography variant="h6" color="textSecondary">
                  {apartment.title}
                </Typography>
              </Grid>

              <Grid item>
                <Grid container direction="row" alignItems="center" spacing={3}>
                  <Grid item>
                    <Typography variant="h6" color="textSecondary">
                      {convertSize(apartment.size!, "m²", measurementUnit)}{" "}
                      {measurementUnit}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <MeasurementSwitch
                      measurementUnit={measurementUnit}
                      onUnitChange={handleUnitChange}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {apartment.rent && (
                <Grid item>
                  <Typography variant="h6" color="textSecondary">
                    <FormattedNumber
                      value={parseFloat(apartment.rent)}
                      // eslint-disable-next-line react/style-prop-object
                      style="currency"
                      currency={apartment.currency}
                    />{" "}
                    {(() => {
                      switch (apartment.listingType) {
                        case "long-term":
                          return <FormattedMessage id="per-month" />;
                        default:
                          return <FormattedMessage id="per-night" />;
                      }
                    })()}
                  </Typography>
                </Grid>
              )}
            </Grid>
          );
          const cardDetailsLeft = (
            <>
              <Typography variant="body1" color="textSecondary">
                <FormattedMessage id="type" />:{" "}
                <FormattedMessage id={apartment.accommodationType} />
              </Typography>
              <Typography variant="body1" color="textSecondary">
                <FormattedMessage id="rental-period" />:{" "}
                <FormattedMessage id={apartment.duration} />
              </Typography>
              <Typography variant="body1" color="textSecondary">
                <FormattedMessage id="gender" />:{" "}
                <FormattedMessage id={apartment.genderPreference} />
              </Typography>
              <Typography variant="body1" color="textSecondary">
                <FormattedMessage id="address" />: {apartment.address},{" "}
                {apartment.city}
              </Typography>
              <Typography variant="body1" color="textSecondary">
                {apartment.country}
              </Typography>
            </>
          );
          return (
            <Grid item xs={12} md={4} key={apartment.id}>
              <BaseCard
                creatorId={apartment.creatorId!}
                images={apartment.photos?.map((photo) => photo.data)}
                title={apartment.title}
                price={apartment.rent}
                currency={apartment.currency}
                listingType={listingType}
                description={apartment.description}
                location={`${apartment.city}, ${apartment.country}`}
                size={apartment.size}
                topDetails={cardDetailsTop}
                details={cardDetailsLeft}
                availableFrom={apartment.availableFrom}
                canDelete={canDelete || userId === apartment.creatorId}
                canModify={userId === apartment.creatorId}
                onDelete={() => handleDeleteApartment(apartment)}
                onEdit={() => handleEditApartment(apartment)}
              />
            </Grid>
          );
        })}
      </Grid>
    </div>
  );
};

export default ApartmentComponent;
